import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, inject, AfterViewInit } from '@angular/core';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { Alert, AlertService, AlertType, ModalComponent } from '@michelin/theme';
import {
  ResetLocationInformationAction,
  UpdateCurrentPaginatedWorkOrdersAction,
  UpdateCurrentPaginatedWorkOrderLinesAction,
  UpdateIsRestoreStateAction,
  UpdateWorkOrderHeaderMetadataAction,
  WorkOrderState
} from '../../../../shared/state/WorkOrderState';
import { WorkOrder } from '../model/workorder';
import { WorkOrderLineView } from '../work-order-line-list/model/work-order-line-view.model';
import { WorkOrderLineExtended } from './model/work-order-line-extended.model';
import { WorkOrderLineExtendedService } from '../../services/work-order-line-extended.service';
import { TabData } from './tabs/tab-data.interface';
import { DealerState } from '../../../../shared/state/DealerState';
import { LocationInformationTabComponent } from './tabs/location-information-tab/location-information-tab.component';
import { CasingHistoryTabComponent } from './tabs/casing-history-tab/casing-history-tab.component';
import { WorkOrderLineTabComponent } from './tabs/work-order-line-tab/work-order-line-tab.component';
import { WorkOrderHeaderMetadata } from '../work-order-line-list/model/paginated-wol.model';
import { BreadcrumbContextService, BreadcrumbItem } from '../../../../shared/services/breadcrumb-context.service';
import { FEATURE_FLAGS } from 'src/app/shared/constants/constants';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { WorkOrderService } from '../../services/workorder.service';
import { PaginatedWOLResponse } from '../work-order-line-list/model/paginated-wol.model';
import { WorkOrderReportDTO } from '../model/work-order-report.model';
import { MatDialog } from '@angular/material/dialog';
import { CamRequest } from 'src/app/features/asset-management/models/request/CamRequest';
import { ActionType } from 'src/app/features/asset-management/models/SpecLine';
import { hasWorkOrderEditRoles, processCodeToWorkType, WorkOrderLineEditViolation } from '../../shared/util/work-order-helper-util';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { UtilityService } from '../../../../shared/services/utility.service';

@Component({
  selector: 'app-work-order-line-details',
  templateUrl: './work-order-line-details.component.html',
  styleUrl: './work-order-line-details.component.scss',
  standalone: false
})
export class WorkOrderLineDetailsComponent implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {
  @ViewChild('workOrderLineTab', { static: false }) workOrderLineTabComponent: WorkOrderLineTabComponent;
  @ViewChild('locationInfoTab', { static: false }) locationInfoTabComponent: LocationInformationTabComponent;
  @ViewChild('finishedProductTab', { static: false }) finishedProductTabComponent: any;
  @ViewChild('auditTrailTab', { static: false }) auditTrailTabComponent: any;
  @ViewChild('casingHistoryTab', { static: false }) casingHistoryTabComponent: CasingHistoryTabComponent;
  @ViewChild('camAlertModal') camAlertModal: ModalComponent;
  @ViewChild('contentModal') contentModal: TemplateRef<any>;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  public translationService = inject(TranslationService);
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public featureflagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public utilityService = inject(UtilityService);
  public workOrderLineExtendedService = inject(WorkOrderLineExtendedService);
  public breadcrumbContextService = inject(BreadcrumbContextService);
  public dialog = inject(MatDialog);
  public workOrderService = inject(WorkOrderService);

  workOrderHeaderForm: FormGroup;
  loading: boolean;
  workOrderId: string;
  workOrderLineId: string;
  dealerId: string;
  workOrder: WorkOrder;
  workOrderLine: WorkOrderLineView;
  workOrderLineExtended: WorkOrderLineExtended | null = null;
  workOrderHeaderMetadata: WorkOrderHeaderMetadata | null = null;
  customerType: string | null = null;
  hasUnsavedChanges: boolean = false;

  tabData: TabData = {};
  tabHasChanges: { [key: string]: boolean } = {};
  tabDataLoaded: { [key: string]: boolean } = {};
  activeTabIndex: number = 0;

  showSinglePageLayout: boolean = false;

  showDueDayField: boolean = false;
  showCustCodeField: boolean = false;
  showMfgDotField: boolean = false;
  showRtDotField: boolean = false;
  workOrderEditEnabled: boolean = false;
  ignoreAgeCamValidation: boolean = false;
  ignoreNtrCamValidation: boolean = false;
  pendingSave: boolean = false;
  ntrValidationFailed: boolean = false;
  ageValidationFailed: boolean = false;
  custCodeFieldLabel: string = 'Ship-to Code'; // legacy fallback
  custCodeFieldLabelKey: string = 'shipToCodeText'; // new translation key
  camViolationError: { fieldName: string; value: any; maximumLimit: any };
  workOrderEditEnabledFlag: boolean = false;
  disposition: string = '';
  rtDotRequired: string = null;
  isDowngradeOption: boolean = false;
  downgradeEligibleTreads: string[] = [];
  downgradeApplication: string = '';
  downgradeAxle: string = '';
  selectedDowngradeTread: string = '';
  downgradeTreadRequired: boolean = false;
  isActionInProgress: boolean = false;
  isAuditTrailEnable = false;
  isCasingHistory: boolean = false;

  // Dynamic breadcrumb support
  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  columnNames: Record<string, string> = {
    lineNumber: 'Line #',
    casingId: 'Bar Code',
    prodCustomer: 'Production Cust',
    processCode: 'PC',
    workType: 'Work Type',
    casingStatus: 'Status',
    shippingLocation: 'Location',
    post: 'Post',
    make: 'Make',
    series: 'Series',
    size: 'Size',
    treadDesc: 'Tread',
    mfgDot: 'MFG DOT',
    application: 'Application',
    axle: 'Axle',
    ntr: 'NTR',
    rdot: 'RT DOT',
    camSpecID: 'CAM Spec ID',
    grade: 'Grade',
    overrideGrade: 'Override Grade',
    sequence: 'Sequence',
    wheelType: 'Wheel Type',
    wheelColor: 'Wheel Colour',
    casingNotes: 'Notes',
    notes: 'Notes',
    warrantyAdjustment: 'Warranty Adj',
    casingValue: 'Casing Value',
    scrapCode: 'Scrap Code',
    npCode: 'NP Code',
    damageLength: 'Damage Length',
    moldType: 'Mold Type',
    width: 'Width',
    length: 'Length',
    disposalCharge: 'Disposal Charge',
    brand: 'Brand #',
    rejectCode: 'Reject Code',
    repairs: 'Repairs'
  };

  exportDataFetcher = (): Observable<ExportResponse> => {
    return new Observable<ExportResponse>((subscriber) => {
      this.workOrderService.searchWorkOrderLines(this.dealerId, this.workOrderId, 1, 10000).subscribe({
        next: (response: PaginatedWOLResponse) => {
          const lines = (response?.data?.workOrderLines || []) as Record<string, any>[];
          const filtered = this.workOrderLineId ? lines.filter((l) => l.id === this.workOrderLineId) : lines;

          if (!filtered || filtered.length === 0 || !this.workOrderId) {
            subscriber.next({ data: filtered } as ExportResponse);
            subscriber.complete();
            return;
          }

          this.workOrderService.getWorkOrderDetails(this.dealerId, this.workOrderId).subscribe({
            next: (report: WorkOrderReportDTO) => {
              const reportLines = (report?.workOrderLines || []) as Record<string, any>[];
              const merged = filtered.map((l) => {
                const match = reportLines.find((rl) => rl.workOrderLinePk === l.id || rl.id === l.id || rl.id === l.workOrderLinePk);
                if (match) {
                  return { ...l, ...match } as Record<string, any>;
                }
                return l;
              });

              subscriber.next({ data: merged } as ExportResponse);
              subscriber.complete();
            },
            error: (err) => {
              subscriber.error(err);
            }
          });
        },
        error: (err) => subscriber.error(err)
      });
    });
  };

  get extraColumns(): Record<string, string> {
    return {
      'WO #': this.workOrder?.workOrderNumber ?? '',
      'Site': this.workOrder?.site ?? '',
      'Plant': this.workOrder?.plant ?? '',
      'Received': this.workOrder?.receivedDate ?? '',
      'Due': this.workOrder?.dueDate ?? '',
      'Customer': this.workOrder?.customerName ?? '',
      'CRN': this.workOrder?.crn ?? ''
    };
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: 'WO-' + (this.workOrder?.workOrderNumber || 'export'),
      extraColumns: this.extraColumns,
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  downgradeForm: FormGroup;

  constructor() {
    this.showDueDayField = false;
    this.showCustCodeField = false;
    this.showMfgDotField = false;
    this.showRtDotField = false;
    this.custCodeFieldLabel = 'Ship-to Code';
    this.custCodeFieldLabelKey = 'shipToCodeText';

    this.workOrderHeaderForm = this.fb.group({
      workOrderNumber: [''],
      workOrderStatus: [''],
      casingStatus: [''],
      plant: [''],
      site: [''],
      receivedDate: [''],
      dueDate: [''],
      crn: [''],
      customerCode: [''],
      customerName: [''],
      customerNote: [''],
      cnpj: [''],
      due: [''],
      lineNumber: [''],
      numberOfLines: [''],
      fiscalIdNum: [''],
      dueDay: ['']
    });
    this.downgradeForm = this.fb.group({
      tread: [{ value: '', disabled: true }, Validators.required],
      application: [''],
      axle: ['']
    });
    this.loading = true;
    this.workOrderId = this.route.snapshot.paramMap.get('workOrderId');
    this.workOrderLineId = this.route.snapshot.paramMap.get('workOrderLineId');
    this.dealerId = this.store.selectSnapshot(DealerState.currentDealer).id;
    this.workOrderEditEnabledFlag =
      this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.WORK_ORDER_EDIT_ENABLED) && hasWorkOrderEditRoles();
    this.isAuditTrailEnable = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.AUDIT_TRAIL_ENABLED);
    this.isCasingHistory = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.CASING_HISTORY_ENABLED);
  }

  ngOnInit(): void {
    this.fetchWorkOrderDetails();
    this.fetchWorkOrderLineExtendedDetails();
    this.setupFormValueChanges();
  }

  ngAfterViewInit(): void {
    this.workOrderLineTabComponent.workOrderLineForm?.get('mfgDOT').valueChanges.subscribe((value) => {
      if (value != null && value.length > 3 && !this.workOrderLineTabComponent.workOrderLineForm.pristine) {
        this.onMfgDOTChange(value);
      }
    });

    this.workOrderLineTabComponent.workOrderLineForm?.get('rtDOT').valueChanges.subscribe((value) => {
      if (value != null && value.length > 6 && !this.workOrderLineTabComponent.workOrderLineForm.pristine) {
        this.onRtDOTChange(value);
      }
    });

    this.workOrderLineTabComponent.workOrderLineForm?.get('ntr').valueChanges.subscribe((value) => {
      if (value != null && value.length > 0 && !this.workOrderLineTabComponent.workOrderLineForm.pristine) {
        this.onNTRandMfgDOTChange(value, '', 'SAVE');
      }
    });
  }

  private setupFormValueChanges(): void {
    // Track form value changes to update unsaved changes status
    this.workOrderHeaderForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateHasUnsavedChanges();
    });
  }

  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
    const tabNames = ['workOrderLine', 'locationInformation', 'finishedProductInformation', 'auditTrail', 'casingHistory'];
    const tabName = tabNames[event.index];

    if (tabName && !this.tabDataLoaded[tabName]) {
      this.triggerTabDataLoad(tabName);
    }
  }

  private triggerTabDataLoad(tabName: string): void {
    if (this.tabDataLoaded[tabName]) return;

    this.tabDataLoaded[tabName] = true;

    const tabComponentMap: { [key: string]: any } = {
      workOrderLine: this.workOrderLineTabComponent,
      locationInformation: this.locationInfoTabComponent,
      finishedProductInformation: this.finishedProductTabComponent,
      auditTrail: this.auditTrailTabComponent,
      casingHistory: this.casingHistoryTabComponent
    };

    const targetComponent = tabComponentMap[tabName];

    if (targetComponent?.loadData instanceof Function) {
      targetComponent.loadData();
    }
  }

  onTabDataChanged(tabName: string, data: any): void {
    this.tabData[tabName] = data;
    this.updateHasUnsavedChanges();
  }

  onTabHasChanges(tabName: string, hasChanges: boolean): void {
    this.tabHasChanges[tabName] = hasChanges;
    this.updateHasUnsavedChanges();
  }

  private updateHasUnsavedChanges(): void {
    const headerFormHasChanges = this.workOrderHeaderForm.dirty;
    const tabsHaveChanges = Object.values(this.tabHasChanges).some((hasChanges) => hasChanges);
    this.hasUnsavedChanges = headerFormHasChanges || tabsHaveChanges;
  }

  fetchWorkOrderDetails(): void {
    const workOrders = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders);
    this.workOrder = workOrders.find((wo) => wo.id === this.workOrderId);

    if (!this.workOrder) {
      this.workOrderHeaderForm.reset();
      return;
    }
    const workOrderLines = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrderLines);
    this.workOrderLine = workOrderLines.find((wol) => wol.id === this.workOrderLineId);
    const getMetadata = this.store.selectSnapshot(WorkOrderState.getWorkOrderHeaderMetadata);
    this.workOrderHeaderMetadata = getMetadata(this.workOrderId);
    this.updateFormWithData();
    this.setupDynamicBreadcrumbs();
    if (
      (this.workOrderEditEnabledFlag && this.workOrderLine.casingStatus == '') ||
      (this.workOrderLine.casingStatus == 'SHOP_FLOOR' && this.workOrderLine.lockPost === null) ||
      this.workOrderLine.casingStatus == 'TRANSFERRED' ||
      this.workOrderLine.casingStatus == 'EDITABLE' ||
      this.workOrderLine.casingStatus == 'RELEASED' ||
      this.workOrderLine.casingStatus == 'INCOMPLETE'
    ) {
      this.workOrderEditEnabled = true;
    } else {
      this.workOrderEditEnabled = false;
    }
  }

  fetchWorkOrderLineExtendedDetails(): void {
    if (this.workOrderLineId && this.dealerId && this.workOrderId) {
      this.loading = true;
      this.workOrderLineExtendedService
        .getExtendedDetails(this.workOrderLineId, this.dealerId, this.workOrderId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (extendedData) => {
            this.workOrderLineExtended = extendedData;
            this.loading = false;
          },
          error: (error) => {
            console.error('Failed to fetch extended work order line details:', error);
            this.workOrderLineExtended = null;
            this.loading = false;
          }
        });
    } else {
      this.workOrderLineExtended = null;
      this.loading = false;
    }
  }

  private updateFormWithData(): void {
    if (this.workOrder) {
      this.customerType = this.workOrder.customerType || null;
      this.workOrderHeaderForm.patchValue({
        ...this.workOrder,
        lineNumber: this.workOrderLine?.lineNumber || '',
        receivedDate: this.workOrder.receivedDate || '',
        dueDate: this.workOrder.dueDate || ''
      });
    }
    if (this.workOrderLine) {
      this.workOrderHeaderForm.patchValue({
        casingStatus: this.workOrderLine?.casingStatus || ''
      });
    }
    if (this.workOrderHeaderMetadata) {
      this.workOrderHeaderForm.patchValue({
        fiscalIdNum: this.workOrderHeaderMetadata.fiscalIdNum || '',
        dueDay: this.workOrderHeaderMetadata.dueDayValue || ''
      });
      // map label id to translation key
      this.custCodeFieldLabelKey = this.getLabelFromId(this.workOrderHeaderMetadata.custCodeLabelId);
      // maintain legacy string (fallback if some other place still uses it)
      this.custCodeFieldLabel = this.translationService.translate(this.custCodeFieldLabelKey) || 'Ship-to Code';
      this.showDueDayField = this.workOrderHeaderMetadata?.showDueDayField || false;
      this.showCustCodeField =
        this.workOrderHeaderMetadata?.custCodeReqValue === 'Y' || this.workOrderHeaderMetadata?.custCodeReqValue === 'R';
      this.showMfgDotField = this.workOrderHeaderMetadata?.showMfgDotField || false;
      this.showRtDotField = this.workOrderHeaderMetadata?.showRtDotField || false;
      this.rtDotRequired = this.workOrderHeaderMetadata.rtDotRequired;
    }
  }

  backNavigation(): void {
    window.history.back();
  }

  onCamCheckRequired(event: { ntr: number; mfgDOT: string }): void {
    this.onNTRandMfgDOTChange(event.ntr, event.mfgDOT, 'SAVE');
  }

  onNTRandMfgDOTChange(ntr: number, mfgDOT: string, action: string): void {
    if (this.performCamCheck()) {
      const workOrderLineDetails = this.tabData?.workOrderLine?.workOrderLine || {};

      const getRawValue = (field: any): string => {
        if (field == null) return '';
        if (typeof field === 'object') return (field.value ?? '').toString().trim();
        return field.toString().trim();
      };

      const getMakeValue = (field: any): string => {
        const raw = getRawValue(field);
        if (!raw) return '';
        const parts = raw.split('-');
        return (parts.length > 1 ? parts[1] : parts[0]).trim();
      };

      const selectedTread: string = getRawValue(workOrderLineDetails.treadDesc);

      const payload: CamRequest = {
        make: getMakeValue(workOrderLineDetails.make),
        series: getRawValue(workOrderLineDetails.series),
        size: getRawValue(workOrderLineDetails.size),
        tread: selectedTread,
        customerCode: !this.hasCustomerOptions() ? this.workOrderHeaderForm.get('customerCode')?.value : null,
        workType: processCodeToWorkType(workOrderLineDetails.processCode),
        application: getRawValue(workOrderLineDetails.application),
        axle: getRawValue(workOrderLineDetails.axle),
        ntr: ntr != null ? ntr : null,
        mdot: mfgDOT || '',
        customerId: this.hasCustomerOptions()
          ? this.workOrderLineTabComponent.prodCustomerOptions.find((option) => option.value === workOrderLineDetails.prodCustomer)?.id ||
            ''
          : '',
        m3Flag: this.workOrderLineTabComponent.getM3Flag(selectedTread),
        ignoreCheckCamSpecNtr: this.ignoreNtrCamValidation,
        ignoreCheckCamSpecAge: this.ignoreAgeCamValidation
      };

      this.workOrderLineExtendedService.getMatchingCamSpecs(this.dealerId, payload).subscribe({
        next: (camResponse) => {
          if (camResponse) {
            if (camResponse.ntr.violated && camResponse.ntr.failureLines.length > 0) {
              this.camViolationError = {
                fieldName: camResponse.ntr.failureLines[0].fieldName,
                value: camResponse.ntr.failureLines[0].value,
                maximumLimit: camResponse.ntr.failureLines[0].maxLimit
              };
              if (!this.ignoreNtrCamValidation) {
                this.ntrValidationFailed = true;
                this.disposition = camResponse.ntr?.disposition;
                if (camResponse.ntr?.action === ActionType.DOWNGRADE || camResponse.ntr?.action === 'DOWNGRADE') {
                  this.isDowngradeOption = true;
                  // Filter treads to only those present in the work order line tab dropdown
                  const availableTreadValues = (this.workOrderLineTabComponent?.treadDescOptions || []).map((opt) => opt.value);
                  this.downgradeEligibleTreads = (camResponse.ntr?.treads || []).filter((tread) => availableTreadValues.includes(tread));
                  if (this.downgradeEligibleTreads.length > 0) {
                    this.downgradeForm.get('tread').enable();
                  }
                  this.downgradeApplication = camResponse.ntr?.application || '';
                  this.downgradeAxle = camResponse.ntr?.axle || '';
                  this.downgradeForm.get('application').setValue(this.downgradeApplication);
                  this.downgradeForm.get('axle').setValue(this.downgradeAxle);
                }
                this.displayAlertModal();
                return; // Stop here — modal will resume save if user ignores
              }
              // Violation already ignored — fall through to check AGE
            }
            if (camResponse.age.violated && camResponse.age.failureLines.length > 0) {
              this.camViolationError = {
                fieldName: camResponse.age.failureLines[0].fieldName,
                value: camResponse.age.failureLines[0].value,
                maximumLimit: camResponse.age.failureLines[0].maxLimit
              };
              if (!this.ignoreAgeCamValidation) {
                this.ageValidationFailed = true;
                this.disposition = camResponse.age?.disposition;
                if (camResponse.age?.action === ActionType.DOWNGRADE || camResponse.age?.action === 'DOWNGRADE') {
                  this.isDowngradeOption = true;
                  // Filter treads to only those present in the work order line tab dropdown
                  const availableTreadValues = (this.workOrderLineTabComponent?.treadDescOptions || []).map((opt) => opt.value);
                  this.downgradeEligibleTreads = (camResponse.age?.treads || []).filter((tread) => availableTreadValues.includes(tread));
                  if (this.downgradeEligibleTreads.length > 0) {
                    this.downgradeForm.get('tread').enable();
                  }
                  this.downgradeApplication = camResponse.age?.application || '';
                  this.downgradeAxle = camResponse.age?.axle || '';
                  this.downgradeForm.get('application').setValue(this.downgradeApplication);
                  this.downgradeForm.get('axle').setValue(this.downgradeAxle);
                }
                this.displayAlertModal();
                return; // Stop here — modal will resume save if user ignores
              }
              // Violation already ignored — fall through
            }
          }
          // No blocking violations — resume pending save if triggered from Save button
          if (this.pendingSave) {
            this.pendingSave = false;
            this.executeActualSave(action);
          }
        },
        error: (camError) => {
          console.error('Error fetching CAM specs:', camError);
          this.isActionInProgress = false;
        }
      });
    }
  }

  onMfgDOTChange(mfgDOT: string): void {
    this.workOrderLineExtendedService.verifyDotDetails(this.dealerId, this.workOrderId, mfgDOT, '').subscribe({
      next: (response) => {
        if (response && !response.data.valid) {
          if (response.data.errorCode.includes('MFG_DOT')) {
            this.workOrderLineTabComponent.workOrderLineForm.controls['mfgDOT'].setErrors({ incorrect: true });
          }
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate(WorkOrderLineEditViolation[response.data.errorCode])
          } as Alert);
        } else if (response && response.data.valid) {
          this.onNTRandMfgDOTChange(null, mfgDOT, 'SAVE');
        }
      },
      error: (error) => {
        console.error('Error occurred during DOT verification:', error);
      }
    });
  }

  onRtDOTChange(rtDOT: string): void {
    this.workOrderLineExtendedService.verifyDotDetails(this.dealerId, this.workOrderId, '', rtDOT).subscribe({
      next: (response) => {
        if (response && !response.data.valid) {
          if (response.data.errorCode.includes('RT_DOT')) {
            this.workOrderLineTabComponent.workOrderLineForm.controls['rtDOT'].setErrors({ incorrect: true });
          }
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate(WorkOrderLineEditViolation[response.data.errorCode])
          } as Alert);
        }
      },
      error: (error) => {
        console.error('Error occurred during DOT verification:', error);
      }
    });
  }

  saveChanges(action: string): void {
    if (this.performCamCheck() && this.tabData?.workOrderLine) {
      this.pendingSave = true;
      const ntr = this.workOrderLineTabComponent.workOrderLineForm.get('ntr')?.value;
      const mfgDOT = this.workOrderLineTabComponent.workOrderLineForm.get('mfgDOT')?.value || '';
      this.onNTRandMfgDOTChange(ntr, mfgDOT, action);
      return;
    }
    this.executeActualSave(action);
  }

  private executeActualSave(action: string): void {
    this.workOrderLineTabComponent.saveChanges(
      this.ignoreAgeCamValidation,
      this.ignoreNtrCamValidation,
      this.camViolationError?.fieldName,
      this.disposition,
      action
    );
  }

  private extractExtendedDetailsWithHeaderPayload(response: any): any {
    return response?.data?.data ?? response?.data ?? response ?? {};
  }

  private mapApiLineToWorkOrderLine(linePayload: any): Partial<WorkOrderLineView> {
    if (!linePayload) return {};

    return {
      id: linePayload.workOrderLinePk,
      casingStatus: linePayload.casingStatus,
      make: linePayload.make,
      series: linePayload.series,
      size: linePayload.size,
      mdot: linePayload.mfgDot
    };
  }

  private mapApiHeaderToWorkOrder(workOrderHeader: any): Partial<WorkOrder> {
    if (!workOrderHeader) return {};

    return {
      id: workOrderHeader.id,
      workOrderNumber: workOrderHeader.workOrderNumber,
      workOrderStatus: workOrderHeader.workOrderStatus,
      numberOfLines: workOrderHeader.numberOfLines,
      customerPk: workOrderHeader.customerPk,
      customerCode: workOrderHeader.customerCode,
      customerName: workOrderHeader.customerName,
      customerTurnAroundTime: workOrderHeader.customerTurnAroundTime,
      crn: workOrderHeader.crn,
      plant: workOrderHeader.plant,
      site: workOrderHeader.site,
      receivedDate: workOrderHeader.receivedDate,
      dueDate: workOrderHeader.dueDate,
      customerNote: workOrderHeader.customerNote,
      customerType: workOrderHeader.customerType,
      fiscalIdNum: workOrderHeader.fiscalIdNum,
      isAllCasingDeleted: workOrderHeader.isAllCasingDeleted
    };
  }

  onSaveComplete(event?: { action: string; success: boolean; response?: any }): void {
    if (!event?.success) {
      this.isActionInProgress = false;
      return;
    }

    if (event.action === 'REJECT') {
      this.isActionInProgress = true;
      this.workOrderEditEnabled = false;

      if (this.workOrderLineId && this.dealerId && this.workOrderId) {
        this.workOrderLineExtendedService
          .getExtendedDetailsWithHeader(this.workOrderLineId, this.dealerId, this.workOrderId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (extendedDetailsWithHeader) => {
              const payload = this.extractExtendedDetailsWithHeaderPayload(extendedDetailsWithHeader);
              const linePayload = payload?.workOrderLineExtendedDetails ?? {};
              const mappedLine = this.mapApiLineToWorkOrderLine(linePayload);
              const updatedStatus = mappedLine?.casingStatus ?? linePayload?.casingStatus;

              if (this.workOrderLine) {
                this.workOrderLine = {
                  ...this.workOrderLine,
                  ...mappedLine,
                  casingStatus: updatedStatus ?? this.workOrderLine.casingStatus
                };
              }

              this.workOrderLineExtended = {
                ...(this.workOrderLineExtended || ({} as WorkOrderLineExtended)),
                ...linePayload,
                id: mappedLine.id ?? this.workOrderLine?.id,
                mdot: mappedLine.mdot ?? this.workOrderLine?.mdot
              };

              this.persistRejectFormValuesToState(updatedStatus, mappedLine);

              const workOrderHeader = payload?.workOrderHeader;
              if (workOrderHeader) {
                const metadata: WorkOrderHeaderMetadata = {
                  dueDayValue: workOrderHeader.dueDayValue,
                  showDueDayField: workOrderHeader.showDueDayField,
                  custCodeReqValue: workOrderHeader.custCodeReqValue,
                  custCodeLabelId: workOrderHeader.custCodeLabelId,
                  showMfgDotField: workOrderHeader.showMfgDotField,
                  showRtDotField: workOrderHeader.showRtDotField,
                  fiscalIdNum: workOrderHeader.fiscalIdNum,
                  isWorkOrderEditable: workOrderHeader.isWorkOrderEditable,
                  rtDotRequired: workOrderHeader.rtDotRequired
                };
                this.workOrderHeaderMetadata = metadata;
                this.store.dispatch(new UpdateWorkOrderHeaderMetadataAction(this.workOrderId, metadata));
              }

              if (workOrderHeader && this.workOrder?.id) {
                const currentWorkOrders = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders) || [];
                const recordCount = this.store.selectSnapshot(WorkOrderState.totalRecords);
                const updatedWorkOrder: WorkOrder = {
                  ...this.workOrder,
                  ...this.mapApiHeaderToWorkOrder(workOrderHeader)
                };

                this.workOrder = updatedWorkOrder;
                const updatedWorkOrders = currentWorkOrders.map((wo) => (wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
                this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(updatedWorkOrders, recordCount));
              }

              this.resetAllForms();
              this.workOrderHeaderForm.patchValue(
                {
                  ...this.workOrder,
                  lineNumber: this.workOrderLine?.lineNumber || '',
                  casingStatus: updatedStatus || this.workOrderLine?.casingStatus || ''
                },
                { emitEvent: false }
              );

              this.utilityService.showAlert(AlertType.success, 'Success', this.translationService.translate('casingRejectSuccessText'));
              this.isActionInProgress = false;
            },
            error: (error) => {
              console.error('Failed to fetch extended details with header after reject:', error);
              const updatedStatus =
                event.response?.data?.casingStatus || event.response?.data?.workOrderLine?.casingStatus || event.response?.data?.status;
              this.persistRejectFormValuesToState(updatedStatus);
              this.resetAllForms();
              if (updatedStatus) {
                this.workOrderHeaderForm.patchValue({ casingStatus: updatedStatus }, { emitEvent: false });
              }
              this.isActionInProgress = false;
            }
          });
      }
    } else {
      this.isActionInProgress = false;
    }
  }

  private persistRejectFormValuesToState(updatedStatus?: string, lineOverrides: Partial<WorkOrderLineView> = {}): void {
    if (!this.workOrderLine) {
      return;
    }

    const normalizeValue = (value: any): string => {
      if (value == null) return '';
      if (typeof value === 'object') return String(value.value ?? '').trim();
      return String(value).trim();
    };

    const form = this.workOrderLineTabComponent?.workOrderLineForm;
    if (!form) return;

    // Extract and normalize all form values
    const normalizedValues = {
      casingId: normalizeValue(form.get('casingId')?.value),
      prodCustomer: normalizeValue(form.get('prodCustomer')?.value),
      processCode: normalizeValue(form.get('processCode')?.value),
      casingStatus: updatedStatus,
      workType: normalizeValue(form.get('workType')?.value),
      make: normalizeValue(form.get('make')?.value),
      series: normalizeValue(form.get('series')?.value),
      size: normalizeValue(form.get('size')?.value),
      treadDesc: normalizeValue(form.get('treadDesc')?.value),
      camSpecID: normalizeValue(form.get('camSpecID')?.value),
      application: normalizeValue(form.get('application')?.value),
      axle: normalizeValue(form.get('axle')?.value),
      ntr: normalizeValue(form.get('ntr')?.value),
      mfgDOT: normalizeValue(form.get('mfgDOT')?.value),
      rtDOT: normalizeValue(form.get('rtDOT')?.value),
      grade: normalizeValue(form.get('grade')?.value),
      overrideGrade: normalizeValue(form.get('overrideGrade')?.value),
      brand: normalizeValue(form.get('brand')?.value),
      sequence: normalizeValue(form.get('sequence')?.value),
      wheelType: normalizeValue(form.get('wheelType')?.value),
      wheelColor: normalizeValue(form.get('wheelColor')?.value),
      casingNotes: normalizeValue(form.get('casingNotes')?.value),
      warrantyAdjustment: normalizeValue(form.get('warrantyAdjustment')?.value),
      casingValue: normalizeValue(form.get('casingValue')?.value),
      scrapCode: normalizeValue(form.get('scrapCode')?.value),
      npCode: normalizeValue(form.get('npCode')?.value),
      damageLength: normalizeValue(form.get('damageLength')?.value),
      moldType: normalizeValue(form.get('moldType')?.value),
      width: normalizeValue(form.get('width')?.value),
      length: normalizeValue(form.get('length')?.value),
      disposalCharge: normalizeValue(form.get('disposalCharge')?.value)
    };

    const updatedLine: WorkOrderLineView = {
      ...this.workOrderLine,
      casingStatus: updatedStatus,
      treadDesc: normalizeValue(lineOverrides?.treadDesc) || normalizedValues.treadDesc || this.workOrderLine.treadDesc,
      casingId: normalizeValue(lineOverrides?.casingId) || normalizedValues.casingId || this.workOrderLine.casingId,
      prodCustomer: normalizeValue(lineOverrides?.prodCustomer) || normalizedValues.prodCustomer || this.workOrderLine.prodCustomer,
      processCode: normalizeValue(lineOverrides?.processCode) || normalizedValues.processCode || this.workOrderLine.processCode,
      workType: normalizeValue(lineOverrides?.workType) || normalizedValues.workType || this.workOrderLine.workType,
      make: normalizeValue(lineOverrides?.make) || normalizedValues.make || this.workOrderLine.make,
      series: normalizeValue(lineOverrides?.series) || normalizedValues.series || this.workOrderLine.series,
      size: normalizeValue(lineOverrides?.size) || normalizedValues.size || this.workOrderLine.size,
      mdot: normalizeValue((lineOverrides as any)?.mdot) || normalizeValue((lineOverrides as any)?.mfgDot) || this.workOrderLine.mdot,
      brand: normalizeValue(lineOverrides?.brand) || normalizedValues.brand || this.workOrderLine.brand
    };

    this.workOrderLine = updatedLine;

    this.workOrderLineTabComponent?.workOrderLineForm?.patchValue(
      {
        ...normalizedValues
      },
      { emitEvent: false }
    );

    const currentLines = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrderLines) || [];
    if (!Array.isArray(currentLines) || currentLines.length === 0) {
      return;
    }

    const updatedLines = currentLines.map((line) => (line.id === updatedLine.id ? { ...line, ...updatedLine } : line));
    this.store.dispatch(new UpdateCurrentPaginatedWorkOrderLinesAction(updatedLines));
  }

  private resetAllForms(): void {
    this.workOrderHeaderForm.markAsPristine();

    const tabComponents = [
      this.workOrderLineTabComponent,
      this.locationInfoTabComponent,
      this.finishedProductTabComponent,
      this.auditTrailTabComponent
    ];

    tabComponents.forEach((tab) => {
      if (tab && typeof tab.resetForms === 'function') {
        tab.resetForms();
      }
    });

    this.tabHasChanges = {};
    this.hasUnsavedChanges = false;
  }

  private getLabelFromId(labelId?: string): string {
    switch (labelId) {
      case 'CNPJ':
        return this.translationService.translate('cnpjText');
      case 'SHIP_TO_CODE':
        return this.translationService.translate('shipToCodeText');
      default:
        return this.translationService.translate('shipToCodeText');
    }
  }

  private setupDynamicBreadcrumbs(): void {
    // Clear casing search context if this is normal navigation (not from casing search)
    // This prevents "Casing List" from showing when navigating from work order menu
    const currentContext = this.breadcrumbContextService.getNavigationContext();
    if (!currentContext || currentContext.source !== 'casing-search') {
      this.breadcrumbContextService.clearNavigationContext();
    }

    // Generate breadcrumbs based on navigation context
    // Pass workOrderId to ensure correct navigation even from breadcrumbs
    // Determine if this is from work order menu flow by checking if no casing search context exists
    const fromWorkOrderMenu = !currentContext || currentContext.source !== 'casing-search';

    this.breadcrumbItems = this.breadcrumbContextService.generateBreadcrumbs(
      'work-order-line-details',
      this.workOrder?.workOrderNumber,
      this.workOrderLine?.casingId,
      this.workOrder?.id, // Pass the work order ID for proper breadcrumb navigation
      fromWorkOrderMenu // true if coming from work order menu flow, false if from casing search
    );
  }

  getPlantId(): string | null {
    let plants = this.store.selectSnapshot(DealerState.plants);
    const currentPlant = this.workOrderHeaderForm.get('plant').value;
    if (currentPlant && currentPlant.includes('-')) {
      const plantCode = currentPlant.split('-')[0].trim();
      return plants.find((plant) => plant.code === plantCode)?.id;
    } else {
      return plants.find((plant) => plant.name === currentPlant)?.id;
    }
  }

  displayAlertModal(): void {
    let modal = this.camAlertModal.open({
      id: 'confirmation',
      width: 750,
      title: this.translationService.translate('camAlertHeaderText'),
      content: this.contentModal
    });

    modal.beforeClosed().subscribe({
      next: (result) => {
        if (result != undefined) {
          this.isActionInProgress = false;
          this.pendingSave = false;
        }
        this.ageValidationFailed = false;
        this.ntrValidationFailed = false;
        // Only reset spinner + state when user dismissed without confirming (Reject/Downgrade sets isActionInProgress=true before closing)
        if (!this.isActionInProgress) {
          this._resetDowngradeState();
        }
      }
    });
  }

  closeModal(action: string): void {
    this.camAlertModal.dialog.closeAll();
    this._resetDowngradeState();
    if (this.camViolationError.fieldName === 'NTR') {
      this.ignoreNtrCamValidation = true;
      this.workOrderLineTabComponent.workOrderLineForm.controls['ntr'].setErrors(null);
    } else if (this.camViolationError.fieldName === 'AGE') {
      this.ignoreAgeCamValidation = true;
      this.workOrderLineTabComponent.workOrderLineForm.controls['mfgDOT'].setErrors(null);
    }
    if (this.pendingSave) {
      this.pendingSave = false;
      this.executeActualSave(action);
    }
  }

  private hasCustomerOptions(): boolean {
    return this.workOrderLineTabComponent.prodCustomerOptionsDropdown.length > 0;
  }

  processReject(): void {
    this.isActionInProgress = true;

    // Restrict Reject if showRtDotField is true and rtDOT is empty/invalid
    if (
      this.rtDotRequired !== null &&
      this.rtDotRequired === 'R' &&
      this.workOrderLineTabComponent.workOrderLineForm.get('ntr').value > 0
    ) {
      const rtDotControl = this.workOrderLineTabComponent.workOrderLineForm.get('rtDOT');
      if (!rtDotControl.value || rtDotControl.invalid) {
        rtDotControl.markAsTouched();
        rtDotControl.setErrors({ required: true });
        this.camAlertModal.dialog.closeAll();
        this.isActionInProgress = false;
        return;
      }
    }
    this.saveChanges('REJECT');
    this.closeModal('REJECT');
  }

  processDowngrade(): void {
    this.isActionInProgress = true;

    if (this.downgradeForm.invalid) {
      this.downgradeForm.markAllAsTouched();
      this.isActionInProgress = false;
      return;
    }
    const treadValue = this.downgradeForm.get('tread').value;
    const applicationValue = this.downgradeForm.get('application').value;
    const axleValue = this.downgradeForm.get('axle').value;
    const selectedTreadOption = this.workOrderLineTabComponent.treadDescOptions.find((opt) => opt.value === treadValue) || null;
    this.workOrderLineTabComponent.workOrderLineForm.patchValue({
      treadDesc: selectedTreadOption,
      application: applicationValue,
      axle: axleValue
    });
    this.workOrderLineTabComponent.workOrderLineForm.markAsDirty();
    this.saveChanges('DOWNGRADE');
    this.camAlertModal.dialog.closeAll();
    this._resetDowngradeState();
  }

  // When a tread is selected, update application and axle in the form
  onTreadSelected(tread: string): void {
    this.downgradeForm.get('tread').setValue(tread);
  }

  private _resetDowngradeState(): void {
    this.isDowngradeOption = false;
    this.downgradeEligibleTreads = [];
    this.downgradeApplication = '';
    this.downgradeAxle = '';
    this.selectedDowngradeTread = '';
    this.downgradeTreadRequired = false;
    if (this.downgradeForm) {
      this.downgradeForm.reset();
      this.downgradeForm.get('tread').disable();
    }
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(
      this.isFormChanged.bind(this),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService
    );
  }

  private isFormChanged(): boolean {
    // Check if the work order line form has unsaved changes
    if (this.workOrderLineTabComponent && this.workOrderLineTabComponent.workOrderLineForm) {
      return this.workOrderLineTabComponent.workOrderLineForm.dirty;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new UpdateIsRestoreStateAction(true));
    this.store.dispatch(new ResetLocationInformationAction());
  }

  private performCamCheck(): boolean {
    let plants = this.store.selectSnapshot(DealerState.plants);
    const currentPlant = this.workOrderHeaderForm.get('plant').value;
    if (currentPlant && currentPlant.includes('-')) {
      const plantCode = currentPlant.split('-')[0].trim();
      return plants.find((plant) => plant.code === plantCode)?.checkCamSpecWo === 'Y';
    } else {
      return plants.find((plant) => plant.name === currentPlant)?.checkCamSpecWo === 'Y';
    }
  }
}
